import { dismissQuickStartModal } from "./quickStartModal";

const PENDING_AUTH_REDIRECT_KEY = "qit:pending-auth-redirect";
const LEGACY_PENDING_QR_SESSION_KEY = "qit:pending-qr-session";
const OAUTH_STATE_KEY = "oauth_state";

const preserveSessionJoinIntent = (path: string): string => {
    if (/^\/(join-session|join|session)(?:[/?#]|$)/.test(path)) dismissQuickStartModal();
    return path;
};

const isSafeInternalPath = (value: string): boolean => {
    return value.startsWith("/") && !value.startsWith("//") && !value.includes("\\");
};

export const getSafeRedirectPath = (value: unknown): string => {
    if (typeof value !== "string") return "";

    const trimmed = value.trim();
    if (!trimmed) return "";

    try {
        const parsed = trimmed.startsWith("http://") || trimmed.startsWith("https://")
            ? new URL(trimmed)
            : new URL(trimmed, "https://qitplaylist.local");
        const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
        return isSafeInternalPath(path) ? path : "";
    } catch {
        return isSafeInternalPath(trimmed) ? trimmed : "";
    }
};

export const storePendingPostAuthRedirect = (path: string, sessionId?: string): void => {
    if (typeof window === "undefined") return;

    const safePath = getSafeRedirectPath(path);
    if (!safePath) return;
    preserveSessionJoinIntent(safePath);

    try {
        window.sessionStorage.setItem(PENDING_AUTH_REDIRECT_KEY, safePath);
        if (sessionId) {
            window.sessionStorage.setItem(LEGACY_PENDING_QR_SESSION_KEY, sessionId);
        }
    } catch {
        // Ignore storage failures; callers still continue to auth.
    }
};

export const buildAuthEntryPath = (mode: "signin" | "signup", redirectPath: string): string => {
    const safeRedirect = getSafeRedirectPath(redirectPath);
    const params = new URLSearchParams({ auth: mode });
    if (safeRedirect) params.set("redirect", safeRedirect);
    return `/?${params.toString()}`;
};

const readPendingPostAuthRedirect = (): string => {
    if (typeof window === "undefined") return "";
    try {
        const pendingRedirect = getSafeRedirectPath(window.sessionStorage.getItem(PENDING_AUTH_REDIRECT_KEY));
        if (pendingRedirect) return preserveSessionJoinIntent(pendingRedirect);

        const pendingSession = window.sessionStorage.getItem(LEGACY_PENDING_QR_SESSION_KEY)?.trim();
        return pendingSession ? `/join-session/${encodeURIComponent(pendingSession)}` : "";
    } catch {
        return "";
    }
};

const encodeOAuthState = (value: object): string => {
    const bytes = new TextEncoder().encode(JSON.stringify(value));
    let binary = "";
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

export const createPostAuthOAuthState = (native = false): string => {
    if (typeof window === "undefined") return "";
    const nonce = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const state = encodeOAuthState({ nonce, redirect: readPendingPostAuthRedirect(), ...(native ? { native: true, createdAt: Date.now() } : {}) });
    window.sessionStorage.setItem(OAUTH_STATE_KEY, state);
    return state;
};

export const getPostAuthRedirectFromOAuthState = (state: unknown): string => {
    if (typeof state !== "string" || !state.trim() || typeof window === "undefined") return "";
    try {
        const normalized = state.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
        const binary = window.atob(padded);
        const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
        const payload = JSON.parse(new TextDecoder().decode(bytes));
        return getSafeRedirectPath(payload?.redirect);
    } catch {
        return "";
    }
};

export const getPostAuthRedirect = (oauthState?: string | null): string => {
    if (typeof window === "undefined") return "/home";

    try {
        const redirectParam = new URLSearchParams(window.location.search).get("redirect")?.trim();
        const safeRedirectParam = getSafeRedirectPath(redirectParam);
        if (safeRedirectParam) return preserveSessionJoinIntent(safeRedirectParam);
    } catch {
        // Ignore malformed query values and use stored fallback.
    }

    try {
        const pendingRedirect = getSafeRedirectPath(window.sessionStorage.getItem(PENDING_AUTH_REDIRECT_KEY));
        if (pendingRedirect) {
            window.sessionStorage.removeItem(PENDING_AUTH_REDIRECT_KEY);
            window.sessionStorage.removeItem(LEGACY_PENDING_QR_SESSION_KEY);
            return preserveSessionJoinIntent(pendingRedirect);
        }

        const pendingSession = window.sessionStorage.getItem(LEGACY_PENDING_QR_SESSION_KEY)?.trim();
        if (pendingSession) {
            window.sessionStorage.removeItem(LEGACY_PENDING_QR_SESSION_KEY);
            return preserveSessionJoinIntent(`/join-session/${encodeURIComponent(pendingSession)}`);
        }

        const stateRedirect = getPostAuthRedirectFromOAuthState(oauthState);
        if (stateRedirect) return preserveSessionJoinIntent(stateRedirect);
    } catch {
        // Ignore storage failures.
    }

    return "/home";
};

// Routing hint only: the receiving app separately requires an exact pending state.
export const isNativeOAuthState = (state: string | null): boolean => {
    if (!state) return false;
    try {
        const normalized = state.replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")));
        const age = Date.now() - Number(payload.createdAt);
        return payload.native === true && typeof payload.nonce === "string" && age >= 0 && age < 10 * 60_000;
    } catch { return false; }
};
